import React, { useEffect, useMemo, useState } from "react";
import DobbyGenericPage from "../../components/DobbyGenericPage";
import {
  Typography,
  Box,
  TextField,
  Link,
  LinearProgress,
  Button,
  Modal,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { apiClient } from "../../utils/apiClient";
import { InternalEmailThreadComponent } from "../../internal/Email/InternalEmailThreadComponent";
import { DemoRate, rateColumns } from "./rateColumnDefinition";
import {
  DataTableColumn,
  DataTableComponent,
} from "../../components/EmailDataTableComponent/DataTableComponent";
import DobbyDemoPage from "../DobbyDemoPage";
import { RatePriceFluctuationComponent } from "./RatePriceFluctuationComponent";
import { DemoRatesAllResponse } from "@services/demo/types";

export const DemoRatesPage = () => {
  const [rates, setRates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );
  const [fluctuationOpen, setFluctuationOpen] = useState(false);
  const [fluctuationBase, setFluctuationBase] = useState<number | null>(null);
  const [scaleFactorWidthRatio, setScaleFactorWidthRatio] = useState(0.5); // Default width ratio
  const [searchFilter, setSearchFilter] = useState("");
  // On mount, check for search param and set searchFilter if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get("search");
    if (searchParam) {
      setSearchFilter(searchParam);
    }
  }, []);
  // const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    fieldName: string;
    direction: "asc" | "desc";
  }>({
    fieldName: "date",
    direction: "desc",
  });

  const filteredRates = useMemo(() => {
    const tokens = searchFilter.toLowerCase().split(/\s+/).filter(Boolean);

    return rates.filter((rate: DemoRate) => {
      const fields = [
        rate.zip_code,
        rate.email,
        rate.city,
        rate.state,
        rate.pricing_person,
        rate.vendor_name,
      ]
        .map((f) => f?.toLowerCase() || "")
        .join(" "); // Combine for easier token matching

      return tokens.every((token) => fields.includes(token));
    });
  }, [rates, searchFilter]);

  const sortRates = (
    rates: any[],
    fieldName: string,
    direction: "asc" | "desc"
  ) => {
    return [...rates].sort((a, b) => {
      const aValue = a[fieldName];
      const bValue = b[fieldName];

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response =
          await apiClient.post<DemoRatesAllResponse>(
            "/demo/rates/all",
            {}
          );
        const fetchedRates = response.rates;

        setRates(
          sortRates(fetchedRates, sortConfig.fieldName, sortConfig.direction)
        );
      } catch (err) {
        setError("Failed to fetch rates");
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []);

  const handleSort = (column: DataTableColumn<any>) => {
    if (!column.fieldName) return;
    const newDirection =
      sortConfig.fieldName === column.fieldName &&
      sortConfig.direction === "asc"
        ? "desc"
        : "asc";

    setSortConfig({
      fieldName: column.fieldName as string,
      direction: newDirection,
    });

    setRates(sortRates(rates, column.fieldName as string, newDirection));
  };

  const handleRowClick = (row: DemoRate) => {
    setSelectedThreadId(row.thread_id);
    setSelectedMessageId(row.message_id);
    setSelectedRowId(row.id);
  };

  const closeThreadView = () => {
    setSelectedThreadId(null);
    setSelectedMessageId(null);
  };

  const onPriceClick = React.useCallback((row: DemoRate) => {
    setFluctuationBase(row.drayage); // or any base value from row
    setFluctuationOpen(true);
  }, []);

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();

    const startX = event.clientX;
    const startFactor = scaleFactorWidthRatio;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      let newFactor = startFactor - deltaX / window.innerWidth;

      newFactor = Math.min(Math.max(newFactor, 0.2), 0.8); // Limit scale factor
      setScaleFactorWidthRatio(newFactor);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const columns = rateColumns(onPriceClick);

  return (
    <DobbyDemoPage fullHeight title=" Rates">
      <Box
        display="flex"
        flexDirection="row"
        height="100%"
        p={2}
        style={{ position: "relative" }}
      >
        <Box style={{ flex: selectedThreadId ? 1 - scaleFactorWidthRatio : 1 }}>
          {loading && (
            <LinearProgress
              style={{ position: "absolute", top: 0, left: 0, width: "100%" }}
            />
          )}
          {error && <Typography color="error">{error}</Typography>}

          {/* Search filters */}
          <Box
            display="flex"
            gap={2}
            mb={2}
            alignContent={"middle"}
            alignItems={"center"}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              sx={{ width: "400px" }}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
            <Typography variant="caption" color="textSecondary">
              Showing {filteredRates.length} rates
              {searchFilter && (
                <Link
                  component="button"
                  variant="caption"
                  onClick={() => setSearchFilter("")}
                  sx={{
                    ml: 1,
                    color: "primary.main",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Clear search
                </Link>
              )}
            </Typography>
          </Box>

          <DataTableComponent
            rows={filteredRates}
            columns={columns}
            showColumnVisibilityToggle
            keyField="id"
            sortColumn={sortConfig.fieldName as any}
            sortDirection={sortConfig.direction}
            selectedRowKey={selectedRowId || undefined}
            onSort={handleSort}
            onRowClick={handleRowClick}
            sx={{ maxHeight: "calc(100% - 50px)" }}
            stickyHeader
          />
        </Box>

        {selectedThreadId && (
          <>
            <Box
              style={{ flex: scaleFactorWidthRatio }}
              sx={{
                position: "relative",
                height: "100%",
              }}
            >
              <SeparatorBar
                onMouseDown={handleMouseDown}
                closeThreadView={closeThreadView}
              />
              <Box
                sx={{
                  height: "100%",
                  overflow: "auto",
                }}
              >
                <Box
                  style={{
                    position: "absolute",
                    // top: (1 / scaleFactorWidthRatio) * 5,
                    top: 0,
                    transformOrigin: "top left",
                    width: `${100 / (scaleFactorWidthRatio * 2)}%`,
                    height: `${100 / (scaleFactorWidthRatio * 2)}%`,
                    transform: `scale(${scaleFactorWidthRatio * 2})`,
                    overflow: "hidden",
                  }}
                >
                  <InternalEmailThreadComponent
                    threadId={selectedThreadId}
                    loadOnlySelectedMessage
                    selectedMessageId={selectedMessageId || undefined}
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Modal open={fluctuationOpen} onClose={() => setFluctuationOpen(false)}>
        <Box
          sx={{
            zIndex: 99999,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid #ccc",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflow: "auto",
            width: "90%",
            maxWidth: "800px",
          }}
        >
          {fluctuationBase !== null && (
            <RatePriceFluctuationComponent
              basePrice={fluctuationBase}
              delta={100}
            />
          )}
        </Box>
      </Modal>
    </DobbyDemoPage>
  );
};

const SeparatorBar: React.FC<{
  onMouseDown: React.MouseEventHandler;
  closeThreadView: React.MouseEventHandler;
}> = ({ onMouseDown, closeThreadView }) => (
  <>
    <Box
      sx={{
        position: "absolute",
        width: "4px",
        height: "100%",
        cursor: "col-resize",
        backgroundColor: "#eee",
        borderRight: "1px solid #666",
        borderLeft: "1px solid #666",
        "&:hover": { backgroundColor: "#999" },
        zIndex: 999,
      }}
      onMouseDown={onMouseDown}
    />
    <Button
      sx={{
        position: "absolute",
        color: "#333",
        backgroundColor: "#eee",
        border: "1px solid",
        borderColor: "#666",
        borderLeft: "1px solid",
        borderLeftColor: "#eee",
        top: "50%",
        left: 5,
        borderRadius: 0,
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
        p: 0.3,
        minWidth: 0,
        height: 40,
        fontSize: 10,
        zIndex: 999,
      }}
      variant="outlined"
      onClick={closeThreadView}
    >
      {/* Close */}
      <ChevronRightIcon sx={{ fontSize: 18 }} />
    </Button>
  </>
);
